import React from "react";
import useSWR, { mutate } from "swr";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/app/DatePicker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash, Sparkle, CheckCircle } from "@phosphor-icons/react";
import { fetcher, fmtN, todayISO } from "./common";

let lineSeq = 0;
const blankLine = () => ({ uid: `ln-${++lineSeq}-${Date.now()}`, account_code: "", label: "", tiers: "", debit: "", credit: "" });

export default function Saisie({ file, journals }) {
  const [journalId, setJournalId] = React.useState("");
  const [date, setDate] = React.useState(todayISO());
  const [label, setLabel] = React.useState("");
  const [lines, setLines] = React.useState([blankLine(), blankLine()]);
  const [saving, setSaving] = React.useState(false);
  const [aiLoading, setAiLoading] = React.useState(false);
  const [filterJournal, setFilterJournal] = React.useState("all");
  const [expanded, setExpanded] = React.useState(null);

  const entriesKey = `/accounting/files/${file.id}/entries${filterJournal !== "all" ? `?journal_id=${filterJournal}` : ""}`;
  const { data: entries = [] } = useSWR(entriesKey, fetcher);
  const { data: accounts = [] } = useSWR(`/accounting/files/${file.id}/accounts`, fetcher);

  const selectedJournal = journals.find((j) => j.id === journalId);
  const totals = lines.reduce((t, l) => ({
    d: t.d + (parseFloat(l.debit) || 0),
    c: t.c + (parseFloat(l.credit) || 0),
  }), { d: 0, c: 0 });
  const balanced = Math.abs(totals.d - totals.c) < 0.01 && totals.d > 0;

  const setLine = (i, k, v) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l)));

  const aiSuggest = async () => {
    if (!label) { toast.error("Saisissez d'abord un libellé"); return; }
    setAiLoading(true);
    try {
      const amount = totals.d || totals.c || null;
      const r = await api.post("/accounting/suggest", {
        label, amount,
        journal_type: selectedJournal?.type || "od",
        chart_type: file.chart_type,
      });
      const s = r.data;
      setLines((ls) => {
        const next = [...ls];
        next[0] = { ...next[0], account_code: s.compte || next[0].account_code, label };
        if (next.length > 1) next[1] = { ...next[1], account_code: s.contrepartie || next[1].account_code, label };
        return next;
      });
      toast.success(s.explication || "Imputation proposée");
      if (s.tva) toast.info(`TVA ${file.tva_rate}% probable — ajoutez une ligne 4452/4431.`);
    } catch { toast.error("Suggestion IA indisponible"); }
    finally { setAiLoading(false); }
  };

  const save = async (status) => {
    setSaving(true);
    try {
      const payload = {
        date, journal_id: journalId, label,
        status,
        lines: lines.filter((l) => parseFloat(l.debit) || parseFloat(l.credit)).map((l) => ({
          account_code: l.account_code.trim(), label: l.label || label, tiers: l.tiers || null,
          debit: parseFloat(l.debit) || 0, credit: parseFloat(l.credit) || 0,
        })),
      };
      await api.post(`/accounting/files/${file.id}/entries`, payload);
      toast.success(status === "valide" ? "Écriture validée" : "Brouillard enregistré");
      setLines([blankLine(), blankLine()]);
      setLabel("");
      mutate(entriesKey);
    } catch (err) { toast.error(err?.response?.data?.detail || "Enregistrement impossible"); }
    finally { setSaving(false); }
  };

  const validateEntry = async (id) => {
    try { await api.post(`/accounting/files/${file.id}/entries/${id}/validate`); mutate(entriesKey); toast.success("Écriture validée"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Validation impossible"); }
  };
  const deleteEntry = async (id) => {
    try { await api.delete(`/accounting/files/${file.id}/entries/${id}`); mutate(entriesKey); toast.success("Écriture supprimée"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Suppression impossible"); }
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
      <div className="xl:col-span-2 border border-zinc-200 bg-white rounded-md p-6 space-y-4">
        <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Nouvelle écriture</div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Journal</Label>
            <Select value={journalId} onValueChange={setJournalId}>
              <SelectTrigger data-testid="entry-journal"><SelectValue placeholder="Choisir…" /></SelectTrigger>
              <SelectContent>{journals.map((j) => (<SelectItem key={j.id} value={j.id}>{j.code} — {j.name}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          <div><Label>Date</Label><DatePicker value={date} onChange={setDate} testid="entry-date" /></div>
        </div>
        <div className="flex gap-2 items-end">
          <div className="flex-1"><Label>Libellé général</Label><Input value={label} onChange={(e) => setLabel(e.target.value)} data-testid="entry-label" placeholder="Paiement loyer juin…" /></div>
          <Button type="button" variant="outline" onClick={aiSuggest} disabled={aiLoading} data-testid="entry-ai-suggest" title="Proposition d'imputation IA">
            <Sparkle size={16} className="mr-1" /> {aiLoading ? "Analyse…" : "IA"}
          </Button>
        </div>

        <datalist id="acc-codes">{accounts.map((a) => (<option key={a.code} value={a.code}>{a.name}</option>))}</datalist>

        <div className="space-y-2">
          <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-[0.18em] text-zinc-500">
            <div className="col-span-3">Compte</div><div className="col-span-3">Libellé</div><div className="col-span-2">Tiers</div><div className="col-span-2">Débit</div><div className="col-span-2">Crédit</div>
          </div>
          {lines.map((l, i) => (
            <div key={l.uid} className="grid grid-cols-12 gap-2 items-center">
              <Input className="col-span-3 h-8 text-sm" list="acc-codes" value={l.account_code} onChange={(e) => setLine(i, "account_code", e.target.value)} data-testid={`entry-line-${i}-account`} placeholder="521" />
              <Input className="col-span-3 h-8 text-sm" value={l.label} onChange={(e) => setLine(i, "label", e.target.value)} placeholder={label || "Libellé"} />
              <Input className="col-span-2 h-8 text-sm" value={l.tiers} onChange={(e) => setLine(i, "tiers", e.target.value)} placeholder="Tiers" />
              <Input className="col-span-2 h-8 text-sm text-right px-1.5 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" type="number" min="0" step="0.01" value={l.debit} onChange={(e) => setLine(i, "debit", e.target.value)} data-testid={`entry-line-${i}-debit`} />
              <div className="col-span-2 flex gap-1">
                <Input className="h-8 text-sm text-right px-1.5 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" type="number" min="0" step="0.01" value={l.credit} onChange={(e) => setLine(i, "credit", e.target.value)} data-testid={`entry-line-${i}-credit`} />
                {lines.length > 2 && <button type="button" onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))} className="text-zinc-400 hover:text-red-700"><Trash size={14} /></button>}
              </div>
            </div>
          ))}
          <button type="button" onClick={() => setLines((ls) => [...ls, blankLine()])} className="text-xs text-zinc-600 hover:text-zinc-900 inline-flex items-center gap-1" data-testid="entry-add-line">
            <Plus size={12} /> Ajouter une ligne
          </button>
        </div>

        <div className="border-t border-zinc-200 pt-3 flex items-center justify-between text-sm">
          <div className="flex gap-6">
            <div><span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 block">Total débit</span><span className="font-medium" data-testid="entry-total-debit">{fmtN(totals.d)}</span></div>
            <div><span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 block">Total crédit</span><span className="font-medium" data-testid="entry-total-credit">{fmtN(totals.c)}</span></div>
          </div>
          <div data-testid="entry-balance-status" className={`text-xs px-2 py-1 rounded ${balanced ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-800"}`}>
            {balanced ? "Équilibrée" : "Non équilibrée"}
          </div>
        </div>

        <div className="flex gap-2">
          <Button onClick={() => save("valide")} disabled={!balanced || !journalId || saving} className="flex-1 bg-[#2E3192] hover:bg-[#1E2066]" data-testid="entry-save">
            {saving ? "Enregistrement…" : "Valider l'écriture"}
          </Button>
          <Button variant="outline" onClick={() => save("brouillard")} disabled={!journalId || saving} data-testid="entry-save-draft">Brouillard</Button>
        </div>
      </div>

      <div className="xl:col-span-3">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Écritures du dossier</div>
          <Select value={filterJournal} onValueChange={setFilterJournal}>
            <SelectTrigger className="w-44 h-8" data-testid="entries-filter-journal"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les journaux</SelectItem>
              {journals.map((j) => (<SelectItem key={j.id} value={j.id}>{j.code} — {j.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div className="border border-zinc-200 bg-white rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                <th className="px-3 py-2">Date</th><th className="px-3 py-2">Pièce</th><th className="px-3 py-2">Libellé</th>
                <th className="px-3 py-2 text-right">Montant</th><th className="px-3 py-2">Statut</th><th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {entries.length === 0 && <tr><td colSpan={6} className="p-6 text-zinc-500">Aucune écriture.</td></tr>}
              {entries.map((e) => (
                <React.Fragment key={e.id}>
                  <tr className="hover:bg-zinc-50 cursor-pointer" data-testid={`entry-row-${e.id}`} onClick={() => setExpanded(expanded === e.id ? null : e.id)}>
                    <td className="px-3 py-2">{e.date}</td>
                    <td className="px-3 py-2 font-mono text-xs">{e.piece_no}</td>
                    <td className="px-3 py-2 truncate max-w-[200px]">{e.label || "—"}</td>
                    <td className="px-3 py-2 text-right">{fmtN(e.total_debit)}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${e.status === "valide" ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"}`}>
                        {e.status === "valide" ? "Validée" : "Brouillard"}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {e.status === "brouillard" && (
                        <>
                          <button data-testid={`entry-validate-${e.id}`} onClick={(ev) => { ev.stopPropagation(); validateEntry(e.id); }} className="text-emerald-700 hover:text-emerald-900 mr-2" title="Valider"><CheckCircle size={16} /></button>
                          <button data-testid={`entry-delete-${e.id}`} onClick={(ev) => { ev.stopPropagation(); deleteEntry(e.id); }} className="text-zinc-400 hover:text-red-700" title="Supprimer"><Trash size={16} /></button>
                        </>
                      )}
                    </td>
                  </tr>
                  {expanded === e.id && (
                    <tr><td colSpan={6} className="bg-zinc-50 px-6 py-3">
                      <table className="w-full text-xs">
                        <tbody>
                          {e.lines.map((l) => (
                            <tr key={`${e.id}-${l.account_code}-${l.label}`} className="border-b border-zinc-100 last:border-0">
                              <td className="py-1 font-mono">{l.account_code}</td>
                              <td className="py-1">{l.account_name}</td>
                              <td className="py-1 text-zinc-500">{l.tiers || ""}</td>
                              <td className="py-1 text-right">{l.debit ? fmtN(l.debit) : ""}</td>
                              <td className="py-1 text-right">{l.credit ? fmtN(l.credit) : ""}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </td></tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
